import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback, useState } from 'react';
import { MenuTrigger } from 'react-aria-components';

import {
  Button,
  ButtonProps,
  Dialog,
  Heading,
  Logo,
  Menu,
  MenuItem,
  Modal,
  ModalOverlay,
  Popover,
  Text,
} from '@/components/core';
import { AddIcon, DeleteIcon, SaveIcon } from '@/components/icons';
import { useGetAgentFromPath } from '@/hooks';
import { toAgentId, useDeleteAgent } from '@/hooks/api';

export type AgentOptionsMenuProps = ButtonProps;

const IAgentOptionsMenu = ({ children, ...rest }: AgentOptionsMenuProps) => {
  const [showOpenDeleteModal, setShowOpenDeleteModal] = useState(false);

  const navigate = useNavigate();

  const { data: currentAgent } = useGetAgentFromPath();
  const { mutateAsync: deleteAgent, isPending } = useDeleteAgent();

  const onDeleteAgent = useCallback(() => {
    if (currentAgent?.agentId) {
      deleteAgent(toAgentId(currentAgent.agentId)).then(() => {
        setShowOpenDeleteModal(false);
        navigate({ to: '/agents' }).catch(console.error);
      });
    }
  }, [currentAgent, deleteAgent, navigate]);

  return (
    <>
      <MenuTrigger>
        <Button variant="plain" {...rest}>
          {children}
        </Button>
        <Popover>
          <Menu>
            <MenuItem variant="plain" onAction={async () => navigate({ to: '/agents/new' })}>
              <AddIcon />
              <Text slot="label">New agent</Text>
            </MenuItem>
            <MenuItem onAction={() => setShowOpenDeleteModal(true)} variant="error">
              <DeleteIcon />
              <Text slot="label">Delete agent</Text>
            </MenuItem>
          </Menu>
        </Popover>
      </MenuTrigger>

      <ModalOverlay isOpen={showOpenDeleteModal} onOpenChange={setShowOpenDeleteModal}>
        <Modal>
          <Dialog className="p-4 outline-none">
            <div className="gap-sm flex items-center">
              <Logo variant="error" size="lg">
                <DeleteIcon />
              </Logo>
              <Heading size="lg">Are you sure?</Heading>
            </div>

            <p className="mt-2 text-sm text-gray-600">
              This action cannot be undone. Do you really want to delete this agent?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onPress={() => setShowOpenDeleteModal(false)}>
                <Text>Cancel</Text>
              </Button>
              <Button variant="danger" isDisabled={isPending} onPress={onDeleteAgent}>
                {isPending ? <SaveIcon /> : null}
                <Text>Delete</Text>
              </Button>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
};

export const AgentOptionsMenu = memo(IAgentOptionsMenu) as typeof IAgentOptionsMenu;
